import { motion } from "motion/react";
import type { SearchBarAnimationConfig } from "@/features/shop/cms/schemas/searchBarPayload.schema";
import { SearchBarVariant } from "@/features/shop/types/searchBar.types";
import { H1 } from "@/shared/ui/typographies/h1";

const DEFAULT_GRADIENT_COLORS = [
  "rgba(124, 58, 237, 1)",
  "rgba(139, 92, 246, 0.95)",
  "rgba(167, 139, 250, 0.9)",
  "rgba(192, 132, 252, 0.85)",
  "rgba(217, 119, 255, 0.75)",
  "rgba(240, 171, 252, 0.6)",
  "rgba(251, 207, 232, 0.4)",
];

const buildGradientBackground = (colors?: string[]) => {
  const palette = colors?.length ? colors : DEFAULT_GRADIENT_COLORS;
  const stops = palette.map((color, index) => {
    const percent = palette.length === 1 ? 0 : Math.round((index / (palette.length - 1)) * 100);
    return `${color} ${percent}%`;
  });
  return `linear-gradient(135deg, ${stops.join(", ")})`;
};

type CatalogueOption = {
  id: number
  name: string
};

type SearchBarSectionProps = {
  children: React.ReactNode
  title?: string
  yeatiImageUrl?: string | null
  animationConfig?: SearchBarAnimationConfig
  gradientColors?: string[]
  variant?: SearchBarVariant
  catalogueOptions?: CatalogueOption[]
  selectedCatalogueId?: number | null
  onCatalogueChange?: (catalogueId: number) => void
  isCatalogueLoading?: boolean
};

export const SearchBarSection = ({
  children,
  title = "Que recherchez-vous ?",
  animationConfig = {
    enabled: true,
    duration: 3.5,
    scale: [1, 1.4, 1],
    opacity: [0.5, 1, 0.5],
    ease: "easeInOut",
  },
  gradientColors = DEFAULT_GRADIENT_COLORS,
  variant,
  catalogueOptions,
  selectedCatalogueId,
  onCatalogueChange,
  isCatalogueLoading = false,
}: SearchBarSectionProps) => {
  const hasCatalogueOptions = catalogueOptions !== undefined;
  const shouldShowCatalogueSelector = variant === "compact"
    && (isCatalogueLoading || hasCatalogueOptions);

  if (variant === "compact") {
    return (
      <section
        className="relative z-20 w-full rounded-md"
        style={{ backgroundImage: buildGradientBackground(gradientColors) }}
      >

        <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-4 md:flex-row md:items-center bg-white/5">
          <div className="flex flex-col gap-3 w-full md:flex-row md:items-center">
            {shouldShowCatalogueSelector && (
              <div className="w-full md:w-56 lg:w-64 shrink-0">
                <div className="relative">
                  <select
                    value={selectedCatalogueId ?? ""}
                    onChange={(event) => onCatalogueChange?.(Number(event.target.value))}
                    disabled={isCatalogueLoading || !catalogueOptions?.length}
                    className="w-full rounded-xl border border-white/60 bg-white/95 px-3 py-4 pr-9 text-sm font-semibold text-gray-900 shadow-md outline-none transition focus:ring-2 focus:ring-white/70 disabled:cursor-not-allowed disabled:opacity-80"
                  >
                    {isCatalogueLoading && (
                      <option value="">Chargement...</option>
                    )}
                    {!isCatalogueLoading && catalogueOptions?.map((catalogue) => (
                      <option key={catalogue.id} value={catalogue.id}>
                        {catalogue.name}
                      </option>
                    ))}
                    {!isCatalogueLoading && (!catalogueOptions || catalogueOptions.length === 0) && (
                      <option value="">Aucun catalogue disponible</option>
                    )}
                  </select>

                </div>
              </div>
            )}

            <div className="w-full md:flex-1">{children}</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative z-20 w-full h-[240px] sm:h-[300px] md:h-[200px] rounded-xl"
      style={{ backgroundImage: buildGradientBackground(gradientColors) }}
    >
      {animationConfig.enabled && (
        <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
          <motion.div
            className="absolute z-0"
            style={{
              bottom: "-150px",
              right: "-150px",
              width: "700px",
              height: "700px",
              filter: "blur(60px)",
              transformOrigin: "100% 100%",
              background: buildGradientBackground(gradientColors),
            }}
            animate={{
              scale: animationConfig.scale,
              opacity: animationConfig.opacity,
            }}
            transition={{
              duration: animationConfig.duration,
              repeat: Infinity,
              ease: animationConfig.ease,
            }}
          />
        </div>
      )}

      <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 py-6 sm:px-6 sm:py-8 md:px-12">
        <H1 className="mb-4 text-center text-white sm:mb-6 lg:mb-8 text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-bold leading-tight">
          {title}
        </H1>

        <div className="relative z-50 w-full max-w-3xl">{children}</div>
      </div>
    </section>
  );
};
